"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Briefcase, Stethoscope, Wrench, Scale, Building2, ShoppingBag, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const industriesData = [{
  id: 1,
  title: "Accountants & Financial Advisors",
  description: "Targeted organic search campaigns tailored for chartered accountants, tax consultants, and financial firms seeking high-value commercial clients.",
  iconName: "Briefcase",
  highlight: "High-intent client acquisition"
}, {
  id: 2,
  title: "Dental Practices & Private Healthcare",
  description: "Dominant local map rankings and specialized patient acquisition SEO for private clinics, cosmetic dentists, and medical specialists.",
  iconName: "Stethoscope",
  highlight: "Local map pack dominance"
}, {
  id: 3,
  title: "Trades & Field Services",
  description: "Aggressive local SEO strategies built for plumbers, electricians, and contractors to capture emergency calls and commercial contracts.",
  iconName: "Wrench",
  highlight: "High-volume emergency leads"
}, {
  id: 4,
  title: "Solicitors & Legal Services",
  description: "Compliant, highly authoritative organic strategies for law firms, family legal advisors, and corporate solicitors looking for direct cases.",
  iconName: "Scale",
  highlight: "Qualified legal enquiry growth"
}, {
  id: 5,
  title: "B2B & Professional Services",
  description: "Complex technical SEO and topical authority frameworks engineered for extended decision cycles and decision-maker lead generation.",
  iconName: "Building2",
  highlight: "Pipeline & lead velocity"
}, {
  id: 6,
  title: "E-commerce & Digital Retailers",
  description: "Scalable category architecture, product schema, and technical optimization to boost high-margin organic revenue and search visibility.",
  iconName: "ShoppingBag",
  highlight: "Revenue-focused search scaling"
}];
export default function Industries() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: string) => {
    const iconProps = {
      className: "size-6 text-primary"
    };
    switch (iconName) {
      case "Briefcase":
        return <Briefcase {...iconProps} />;
      case "Stethoscope":
        return <Stethoscope {...iconProps} />;
      case "Wrench":
        return <Wrench {...iconProps} />;
      case "Scale":
        return <Scale {...iconProps} />;
      case "Building2":
        return <Building2 {...iconProps} />;
      case "ShoppingBag":
        return <ShoppingBag {...iconProps} />;
      default:
        return <Briefcase {...iconProps} />;
    }
  };
  return <section id="industriesserved" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border transition-colors duration-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-border">
            Tailored SEO Expertise
          </Badge>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl font-default">
            Industry-Specific SEO Consultancy
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Every sector operates with unique search behaviors, decision cycles, and search intent. I design custom SEO strategies engineered for your specific industry dynamics.
          </p>
        </div>

        {/* Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {industriesData.map((industry, index) => <motion.div key={industry.id} variants={itemVariants} data-index={index}>
              <Card className="h-full bg-card text-card-foreground border border-border hover:border-primary/50 shadow-sm transition-[border-color,box-shadow] duration-300 rounded-xl overflow-hidden flex flex-col justify-between">
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-lg bg-muted border border-border flex items-center justify-center shrink-0">
                      {renderIcon(industry.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs font-medium text-muted-foreground border-border bg-background">
                      {industry.highlight}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default">
                    {industry.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-6 flex-1 flex flex-col justify-between">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed font-default">
                    {industry.description}
                  </CardDescription>

                  <div className="pt-4 border-t border-border flex items-center text-xs font-medium text-foreground">
                    <CheckCircle2 className="size-4 text-primary mr-2 shrink-0" />
                    <span>Direct consultant access &amp; custom strategy</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* CTA Strip */}
        <div className="mt-12 sm:mt-16 bg-muted/50 border border-border rounded-xl p-6 sm:p-8 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-xl font-semibold text-foreground font-default">
              Don&apos;t See Your Exact Sector Listed?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-default">
              I partner directly with professional service firms, localized trades, and enterprise clients across the UK and internationally.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book a Free Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}